import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { authAPI } from "@/lib/api";
import MarketingTopBar from "@/components/MarketingTopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogOut, Mail, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export default function PendingApproval() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const emailVerified = !!user?.email_verified;

  const handleLogout = async () => {
    await logout?.();
    navigate("/login");
  };

  const runVerify = async (payload) => {
    setVerifying(true);
    try {
      await authAPI.verifyEmail(payload);
      toast.success("Email verified!");
      try { await refreshUser?.(); } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const token = params.get("token");
    if (token && !emailVerified) runVerify({ token });
     
  }, [params]);

  const submitCode = (e) => {
    e.preventDefault();
    if (!user?.email || !code) return;
    runVerify({ email: user.email, code });
  };

  const resend = async () => {
    if (!user?.email) return;
    setResending(true);
    try {
      await authAPI.resendVerification({ email: user.email });
      toast.success("Verification code sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <MarketingTopBar
        compact
        authButtons={[{ label: "Verify account", to: "/pending-approval", variant: "primary", testId: "marketing-verify" }]}
      />
      <svg aria-hidden viewBox="0 0 400 400" className="pointer-events-none absolute -right-24 top-8 h-[520px] w-[520px] text-primary/[0.06]">
        <path d="M60 380 L220 60 M120 380 L280 60 M180 380 L340 60 M240 380 L400 60" stroke="currentColor" strokeWidth="14" fill="none" strokeLinecap="round" />
      </svg>
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/3 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />

      <div className="relative mx-auto flex max-w-3xl flex-col items-start px-6 pt-24 pb-16 sm:pt-28 sm:pb-24">
        <h1 className={`font-display text-4xl font-bold uppercase leading-[0.95] sm:text-6xl`}>
          {!emailVerified ? (
            <>Check your inbox and verify your email</>
          ) : (
            <>Your application is under review</>
          )}
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground/80">
          {!emailVerified ? (
            <>Welcome to Sim Racing Connected{user?.username ? `, ${user.username}` : ""}. We just sent a 6-digit code to <strong>{user?.email || "your email"}</strong>. Enter it below, or click the link in the email from a PC.</>
          ) : (
            <>Thanks for verifying{user?.username ? `, ${user.username}` : ""}. Every account is manually reviewed. Review is usually done within 1-2 hours but can take up to 24 hours.</>
          )}
        </p>

        {!emailVerified && (
          <form onSubmit={submitCode} className="mt-8 flex w-full max-w-md flex-col gap-3 rounded-xl border border-border bg-card/70 p-4 sm:flex-row sm:items-center">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              maxLength={6}
              className="text-center font-mono text-lg tracking-[0.4em]"
              data-testid="verify-code-input"
            />
            <Button type="submit" disabled={verifying || code.length !== 6} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="verify-code-submit">
              {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1 h-4 w-4" /> Verify</>}
            </Button>
          </form>
        )}

        {emailVerified && (
          <div className="mt-8 w-full max-w-2xl space-y-4">
            <div>
              <p className="font-display text-sm font-bold uppercase tracking-wider text-foreground/90">Why manual approval?</p>
              <p className="mt-2 text-sm leading-relaxed text-foreground/80">
                We don&apos;t automate joining. Every application is reviewed by our team to prevent bots and keep our community genuine.
              </p>
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-3">
          {!emailVerified && (
            <Button variant="outline" onClick={resend} disabled={resending} data-testid="verify-resend">
              {resending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Resend code
            </Button>
          )}
          <a href="mailto:support@pitlanesimracing.com" data-testid="pending-contact">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Mail className="mr-2 h-4 w-4" /> Contact SRC support
            </Button>
          </a>
          {user && (
            <Link to="/profile" data-testid="pending-profile">
              <Button variant="outline">View profile <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          )}
          <Button variant="ghost" onClick={handleLogout} data-testid="pending-logout">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>

        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground/50">
          Pit Lane Sim Racing · Members-only community
        </p>
      </div>
    </div>
  );
}
